import axios from 'axios';
import { authService } from './authService';

// Definir URL base da API
const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/meu-esporte/us-central1/api';

// Criar instância do axios
const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  async (config) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.token) {
        // Verificar se o token está próximo de expirar (5 minutos)
        const tokenData = JSON.parse(atob(user.token.split('.')[1]));
        const expirationTime = tokenData.exp * 1000;
        const fiveMinutes = 5 * 60 * 1000;
        
        if (Date.now() + fiveMinutes >= expirationTime) {
          // Token vai expirar em breve, tentar renovar
          try {
            const newToken = await authService.refreshToken();
            if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
              return config;
            }
          } catch (refreshError) {
            console.error('Erro ao renovar token:', refreshError);
          }
        }
        
        config.headers.Authorization = `Bearer ${user.token}`;
      }
      return config;
    } catch (error) {
      console.error('Erro no interceptor:', error);
      return Promise.reject(error);
    }
  },
  error => {
    console.error('Erro na interceptação da requisição:', error);
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    console.error('Erro na requisição:', {
      url: originalRequest.url,
      method: originalRequest.method,
      status: error.response?.status,
      code: error.code
    });

    // Retry em caso de timeout (máximo 2 tentativas)
    if (error.code === 'ECONNABORTED' && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest.timeout = 90000; // Aumenta timeout na retry para 90 segundos
      return api(originalRequest);
    }

    // Evitar loop infinito
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (error.response?.status === 500 || error.response?.status === 401) {
      originalRequest._retry = true;

      try {
        // Verificar token atual
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user?.token) {
          throw new Error('Token não encontrado');
        }

        const isValid = await authService.verifyToken(user.token);
        if (!isValid) {
          const newToken = await authService.refreshToken();
          if (newToken) {
            // Atualizar token no localStorage e headers
            localStorage.setItem('user', JSON.stringify({ ...user, token: newToken }));
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            // Refazer a requisição com novo token
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Erro ao renovar token:', refreshError);
        window.location.href = '/login?expired=true';
      }
    }
    
    // Se o token expirou ou está inválido
    if (error.response?.status === 401) {
      // Podemos redirecionar para login ou refrescar o token
      console.error('Sessão expirada ou token inválido');
      
      // Se for uma resposta específica de token inválido ou expirado
      if (error.response.data?.code === 'TOKEN_EXPIRED' || error.response.data?.code === 'INVALID_TOKEN') {
        localStorage.removeItem('@MeuEsporte:token');
        localStorage.removeItem('@MeuEsporte:user');
        
        // Redirecionar para login após um pequeno delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
